"use client";

import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import Image from "next/image";
import Link from "next/link";
import {
  FaLinkedin,
  FaXTwitter,
  FaInstagram,
  FaYoutube,
  FaFacebookF,
} from "react-icons/fa6";
import { Mail, ArrowRight } from "lucide-react";

const FOOTER_LINKS = {
  Product: [
    { label: "Features", href: "/features" },
    { label: "Modules", href: "/#modules" },
    { label: "Pricing", href: "/pricing" },
    { label: "Security", href: "/security" },
  ],
  Company: [
    { label: "About us", href: "/about" },
    { label: "Careers", href: "/careers" },
    { label: "Contact", href: "/contact" },
    { label: "Book a demo", href: "/book-demo" },
  ],
  Resources: [
    { label: "Documentation", href: "#" },
    { label: "Blog", href: "#" },
    { label: "Help Center", href: "#" },
    { label: "FAQ", href: "/#faq" },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
    { label: "GDPR", href: "/gdpr" },
  ],
};
const SOCIAL_LINKS = [
  { icon: FaLinkedin, href: "#" },
  { icon: FaXTwitter, href: "#" },
  { icon: FaInstagram, href: "#" },
  { icon: FaYoutube, href: "#" },
  { icon: FaFacebookF, href: "#" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const logo =
    theme === "light"
      ? "/logo/aeronpay-hrms-logo-white.png"
      : "/logo/aeronpay-hrms-logo-dark.png";

  return (
    <footer className="border-t border-border bg-foreground text-background">
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <div className="grid md:grid-cols-2 gap-8 items-center pb-14 mb-14 border-b border-background/10">
          <div>
            <h3 className="text-2xl font-semibold mb-2">
              Stay ahead on HR trends
            </h3>
            <p className="text-sm text-background/60">
              One email a month. Product updates, compliance changes, no spam.
            </p>
          </div>
          <form
            className="flex gap-3 max-w-md md:ml-auto w-full"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="relative flex-1">
              <Mail
                size={16}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-background/40"
              />
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-background/10 border border-background/20 rounded-lg pl-10 pr-4 py-3 text-sm text-background placeholder:text-background/40 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
              />
            </div>
            <button className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:opacity-90 transition-opacity shrink-0">
              Subscribe <ArrowRight size={14} />
            </button>
          </form>
        </div>

        <div className="grid md:grid-cols-6 gap-10 mb-14">
          <div className="md:col-span-2">
            <Link href="/" className="inline-block mb-4">
              <div className="relative w-[180px] h-[60px]">
                {mounted && (
                  <Image
                    src={logo}
                    alt="AeronPay HRMS"
                    fill
                    className="object-contain object-left"
                  />
                )}
              </div>
            </Link>
            <p className="text-sm text-background/60 leading-relaxed mb-6 max-w-xs">
              The enterprise HR platform built for teams who are done stitching
              spreadsheets together. Trusted by 200+ growing companies.
            </p>
            <div className="flex gap-3">
              {SOCIAL_LINKS.map(({ icon: Icon, href }, i) => (
                <Link
                  key={i}
                  href={href}
                  className="group w-10 h-10 rounded-lg bg-background/10 border border-background/10 flex items-center justify-center transition-all duration-300 hover:bg-primary hover:border-primary"
                >
                  <Icon
                    size={16}
                    className="text-background transition-colors duration-300"
                  />
                </Link>
              ))}
            </div>
          </div>

          {Object.entries(FOOTER_LINKS).map(([section, links]) => (
            <div key={section}>
              <h4 className="text-sm font-semibold mb-4">{section}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      href={link.href}
                      className="text-sm text-background/60 hover:text-background transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-background/10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <p className="text-sm text-background/50">
            © {new Date().getFullYear()} AeronPay HRMS, a product of AeronFly
            International Private Limited. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
